import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import { nanoid } from "nanoid";
import type {
  SchemeContent,
  SchemeStation,
  LocalizedString,
  SchemeManifest,
  SchemeSlotDef,
  SlotType,
  SlotSize,
  SlotFill,
  BackgroundMode,
  InstagramDisplay,
  InstagramConnectionStatus,
} from "@shared/types/foyer";
import { usePremiseScope } from "../../context/PremiseScopeContext";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import AssetField from "../AssetField";
import LocalizedTextField from "../LocalizedTextField";
import {
  foyerApi,
  type AppRecord,
  type RadioStationRecord,
} from "../../http/foyer";
import type { RoomOffer } from "@shared/types/roomOffer";
import { resolveLocalized } from "@shared/i18n";
import { orderCatalogue } from "@shared/catalogue-order";
import { CARE_ICON_OPTIONS } from "@shared/schemes/careIcons";
import { fieldGrid, EDITOR_MAX_WIDTH } from "./editorLayout";
import LocalizedSectionsEditor from "./LocalizedSectionsEditor";
import CatalogueGrid from "./CatalogueGrid";
import {
  certificationOf,
  certificationReason,
} from "@shared/app-certification";
import EditorCard from "./EditorCard";
import { MAX_SLOT_FILLS, slotFills } from "@shared/schemeSlots";
import { tokens } from "../../theme";

/**
 * The structured content a scheme renders, split into standalone panes
 * (Branding / Apps / Radio / Information) so each can be its own sub-section in
 * the editor. Every pane takes the full `SchemeContent` and emits the full
 * updated content, editing only its slice. The content shape is shared by all
 * templates - only the design tokens vary per-template.
 */
export interface ContentPaneProps {
  content: SchemeContent;
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (next: SchemeContent) => void;
}

// ── Branding ─────────────────────────────────────────────────────────────

export function BrandingEditor({ content, onChange }: ContentPaneProps) {
  const setBranding = (part: Partial<SchemeContent["branding"]>) =>
    onChange({ ...content, branding: { ...content.branding, ...part } });
  const b = content.branding;
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Branding</Typography>
      <Box sx={fieldGrid(280)}>
        <TextField
          size="small"
          fullWidth
          label="Hotel name"
          value={b.hotelName}
          onChange={(e) => setBranding({ hotelName: e.target.value })}
        />
        <TextField
          size="small"
          fullWidth
          label="City"
          value={b.city ?? ""}
          onChange={(e) => setBranding({ city: e.target.value })}
        />
        <AssetField
          label="Logo"
          value={b.logoUrl ?? ""}
          preview
          onChange={(url) => setBranding({ logoUrl: url })}
        />
      </Box>
    </Stack>
  );
}

// ── Background & photos ──────────────────────────────────────────────────

/**
 * Choose the home background (solid colour / photo / photo + tint) and pick the
 * template's photo(s) from the media library. Only templates that declare image
 * slots (the photo-forward launchers) expose anything here.
 */
export function BackgroundEditor({
  content,
  manifest,
  onChange,
}: ContentPaneProps & { manifest: SchemeManifest }) {
  const slots = manifest.imageSlots ?? [];
  const mode: BackgroundMode = content.background?.mode ?? "photoTint";
  const setMode = (m: BackgroundMode) =>
    onChange({ ...content, background: { ...content.background, mode: m } });
  const setImage = (id: string, url: string) =>
    onChange({
      ...content,
      images: { ...(content.images ?? {}), [id]: url },
    });

  if (slots.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This template doesn&rsquo;t use a background photo.
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      <Typography variant="h6">Background &amp; photos</Typography>
      <TextField
        size="small"
        select
        label="Home background"
        value={mode}
        onChange={(e) => setMode(e.target.value as BackgroundMode)}
        sx={{ maxWidth: 280 }}
        helperText="How the home screen is filled."
      >
        <MenuItem value="color">Solid colour (no photo)</MenuItem>
        <MenuItem value="photo">Photo only</MenuItem>
        <MenuItem value="photoTint">Photo + tint (recommended)</MenuItem>
      </TextField>
      <Box sx={fieldGrid(300)}>
        {slots.map((slot) => (
          <AssetField
            key={slot.id}
            label={slot.label + (slot.aspect ? ` (${slot.aspect})` : "")}
            value={content.images?.[slot.id] ?? ""}
            preview
            onChange={(url) => setImage(slot.id, url)}
          />
        ))}
      </Box>
    </Stack>
  );
}

// ── Apps ─────────────────────────────────────────────────────────────────

/**
 * Apps are the global catalogue, toggled on/off per premise - same model as the
 * global radio channels. There are no custom apps: the catalogue is edited
 * centrally (Apps page); here you only choose which ones show on this premise,
 * and in what order (`appOrder` - drag a tile).
 */
export function AppsEditor({
  content,
  global,
  isCare = false,
  onChange,
}: ContentPaneProps & {
  /** The global catalogue, fetched once by `TemplateEditor` and shared with the
   *  Screens board so the two cannot disagree about what is pinned. `null`
   *  while it is still loading. */
  global: AppRecord[] | null;
  /** A care premise is never offered a certified app (NFLX-6). */
  isCare?: boolean;
}) {
  const disabled = content.disabledAppIds ?? [];
  const isOn = (id: string) => !disabled.includes(id);
  const toggle = (id: string) =>
    onChange({
      ...content,
      disabledAppIds: isOn(id)
        ? [...disabled, id]
        : disabled.filter((x) => x !== id),
    });

  // A certified app that is ON is pinned first and cannot be dragged (NFLX-4).
  // It is still the operator's to switch OFF - the agreement asks that Netflix
  // come first where it is carried, not that a hotel carry it - so this is a
  // pin, never a lock, and the switch below stays live.
  const pinnedBy = useMemo(() => {
    const m = new Map<string, string>();
    if (isCare) return m;
    for (const a of global ?? []) {
      const cert = certificationOf(a);
      if (cert && !disabled.includes(a.id))
        m.set(a.id, certificationReason(cert));
    }
    return m;
    // `disabled` is derived from `content` each render, so key the memo on the
    // stored array rather than on a fresh one.
  }, [global, content.disabledAppIds, isCare]);

  // A certified app is not offered at all on a care premise (NFLX-6): the
  // agreement it ships under is built on check-in/check-out, and a ward has no
  // check-in clock. Removed from the grid rather than shown switched off - an
  // off switch invites someone to turn it on, and the server would refuse.
  const offerable = useMemo(
    () =>
      isCare
        ? (global ?? []).filter((a) => certificationOf(a) === null)
        : (global ?? []),
    [global, isCare]
  );

  // The grid shows the catalogue in the order the guest gets it - off entries
  // included, so a tile returns to its place when it's switched back on. A
  // pinned entry is hoisted to the head exactly as `withGlobalApps` does, so
  // the board shows the arrangement the guest will actually get rather than the
  // one the stored order asks for.
  const ordered = useMemo(() => {
    const list = orderCatalogue(offerable, content.appOrder);
    if (!pinnedBy.size) return list;
    return [
      ...list.filter((a) => pinnedBy.has(a.id)),
      ...list.filter((a) => !pinnedBy.has(a.id)),
    ];
  }, [offerable, content.appOrder, pinnedBy]);
  const hasOrder = (content.appOrder?.length ?? 0) > 0;
  const onCount = offerable.filter((g) => isOn(g.id)).length ?? 0;

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      <Typography variant="h6">Apps</Typography>

      <EditorCard
        eyebrow="Global apps"
        hint={
          pinnedBy.size
            ? "Pick which apps this premise shows and drag the tiles into the order the guest sees them in. Edit the apps themselves on the catalogue page. One app is pinned first for as long as it is switched on — switch it off and the pin goes with it."
            : "Pick which apps this premise shows and drag the tiles into the order the guest sees them in. Edit the apps themselves on the catalogue page."
        }
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            {global && (
              <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
                {onCount}/{global.length} on
              </Typography>
            )}
            {hasOrder && (
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => onChange({ ...content, appOrder: [] })}
              >
                Sort A–Z
              </Link>
            )}
            <Link
              component={RouterLink}
              to="/apps"
              variant="body2"
              underline="hover"
            >
              Manage catalogue →
            </Link>
          </Stack>
        }
      >
        {global === null ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : global.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No apps yet.
          </Typography>
        ) : (
          <CatalogueGrid
            data-testid="apps-grid"
            items={ordered}
            noun="app"
            isOn={isOn}
            onToggle={toggle}
            pinnedBy={pinnedBy}
            onReorder={(appOrder) => onChange({ ...content, appOrder })}
          />
        )}
      </EditorCard>
    </Stack>
  );
}

// ── Radio stations ─────────────────────────────────────────────────────────

export function StationsEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const loc = { supportedLocales, defaultLocale };
  // Per-premise CUSTOM channels. Add/edit happens in a popup dialog - no inline
  // fields in the pane (`dialogIdx`: "new" = adding, number = editing a row).
  const stations = content.stations;
  const setStations = (s: SchemeStation[]) =>
    onChange({ ...content, stations: s });
  const [dialogIdx, setDialogIdx] = useState<number | "new" | null>(null);
  const saveStation = (st: SchemeStation) => {
    setStations(
      dialogIdx === "new"
        ? [...stations, st]
        : stations.map((s, idx) => (idx === dialogIdx ? st : s))
    );
    setDialogIdx(null);
  };

  // Per-premise on/off selection over the global catalogue. Global channels are
  // edited centrally (catalogue page) - here you only toggle which ones show.
  const disabled = content.disabledStationIds ?? [];
  const isOn = (id: string) => !disabled.includes(id);
  const toggle = (id: string) =>
    onChange({
      ...content,
      disabledStationIds: isOn(id)
        ? [...disabled, id]
        : disabled.filter((x) => x !== id),
    });

  const [global, setGlobal] = useState<RadioStationRecord[] | null>(null);
  useEffect(() => {
    let alive = true;
    foyerApi
      .listRadioStations()
      .then((list) => alive && setGlobal(Array.isArray(list) ? list : []))
      .catch(() => alive && setGlobal([]));
    return () => {
      alive = false;
    };
  }, []);

  // Same contract as Apps. Custom channels are NOT in `stationOrder` - they
  // follow the global list in their own array order (see `withGlobalStations`).
  const ordered = useMemo(
    () => orderCatalogue(global ?? [], content.stationOrder),
    [global, content.stationOrder]
  );
  const hasOrder = (content.stationOrder?.length ?? 0) > 0;
  const onCount = global?.filter((g) => isOn(g.id)).length ?? 0;

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      <Typography variant="h6">Radio</Typography>

      {/* Global catalogue - toggle on/off per premise; not editable here. */}
      <EditorCard
        eyebrow="Global channels"
        hint="Pick which global channels this premise shows and drag them into the order the guest sees them in. Edit the channels themselves on the catalogue page."
        action={
          <Stack direction="row" alignItems="center" spacing={2}>
            {global && (
              <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
                {onCount}/{global.length} on
              </Typography>
            )}
            {hasOrder && (
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => onChange({ ...content, stationOrder: [] })}
              >
                Sort A–Z
              </Link>
            )}
            <Link
              component={RouterLink}
              to="/radio-stations"
              variant="body2"
              underline="hover"
            >
              Manage catalogue →
            </Link>
          </Stack>
        }
      >
        {global === null ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : global.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No global channels yet.
          </Typography>
        ) : (
          <CatalogueGrid
            data-testid="stations-grid"
            items={ordered}
            noun="channel"
            isOn={isOn}
            onToggle={toggle}
            onReorder={(stationOrder) => onChange({ ...content, stationOrder })}
          />
        )}
      </EditorCard>

      {/* Per-premise custom channels - compact rows; edit/add via dialog. */}
      <EditorCard
        eyebrow={`Custom channels - this premise (${stations.length})`}
      >
        {stations.length > 0 && (
          <Stack
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {stations.map((s, i) => (
              <Stack
                key={s.id}
                direction="row"
                alignItems="center"
                sx={{
                  pl: 1.5,
                  pr: 0.5,
                  py: 0.5,
                  borderTop: i === 0 ? 0 : "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  onClick={() => setDialogIdx(i)}
                  sx={{ flex: 1, cursor: "pointer" }}
                >
                  <Typography variant="body2">{s.name}</Typography>
                  {(() => {
                    const tag = resolveLocalized(
                      s.tag,
                      defaultLocale,
                      defaultLocale
                    );
                    return tag ? (
                      <Typography variant="caption" color="text.secondary">
                        {tag}
                      </Typography>
                    ) : null;
                  })()}
                </Stack>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => setDialogIdx(i)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label="Delete station"
                    onClick={() =>
                      setStations(stations.filter((_, idx) => idx !== i))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
        <Button
          startIcon={<AddIcon />}
          onClick={() => setDialogIdx("new")}
          sx={{ alignSelf: "flex-start", mt: stations.length > 0 ? 1.5 : 0 }}
        >
          Add custom channel
        </Button>
      </EditorCard>

      {dialogIdx !== null && (
        <CustomStationDialog
          initial={dialogIdx === "new" ? null : stations[dialogIdx]}
          {...loc}
          onClose={() => setDialogIdx(null)}
          onSave={saveStation}
        />
      )}
    </Stack>
  );
}

/** Add/edit one per-premise custom channel in a popup - same fields as the
 *  global catalogue, so a custom channel behaves like a global one. */
export function CustomStationDialog({
  initial,
  supportedLocales,
  defaultLocale,
  onClose,
  onSave,
}: {
  initial: SchemeStation | null;
  supportedLocales: string[];
  defaultLocale: string;
  onClose: () => void;
  onSave: (s: SchemeStation) => void;
}) {
  const loc = { supportedLocales, defaultLocale };
  const [name, setName] = useState(initial?.name ?? "");
  const [tag, setTag] = useState<LocalizedString>(initial?.tag ?? "");
  const [streamUrl, setStreamUrl] = useState(initial?.streamUrl ?? "");
  const [metadataId, setMetadataId] = useState(initial?.metadataId ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");

  const save = () =>
    onSave({
      id: initial?.id ?? nanoid(6),
      name: name.trim() || "Untitled channel",
      tag,
      freq: initial?.freq ?? "",
      streamUrl: streamUrl.trim() || undefined,
      metadataId: metadataId.trim() || undefined,
      logoUrl: logoUrl.trim() || undefined,
    });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {initial ? "Edit custom channel" : "New custom channel"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <LocalizedTextField
            label="Tag"
            value={tag}
            {...loc}
            onChange={(v: LocalizedString) => setTag(v)}
          />
          <TextField
            size="small"
            label="Stream URL"
            placeholder="https://…"
            value={streamUrl}
            onChange={(e) => setStreamUrl(e.target.value)}
          />
          <TextField
            size="small"
            label="Metadata ID (Sveriges Radio)"
            value={metadataId}
            onChange={(e) => setMetadataId(e.target.value)}
          />
          <AssetField
            label="Logo"
            value={logoUrl}
            preview
            onChange={(url: string) => setLogoUrl(url)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>
          {initial ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Information directory ───────────────────────────────────────────────────

export function InfoEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const sections = content.infoSections;
  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Typography variant="h6">
        Information directory ({sections.length})
      </Typography>
      <LocalizedSectionsEditor
        sections={sections}
        supportedLocales={supportedLocales}
        defaultLocale={defaultLocale}
        showEmbedUrl
        onChange={(s) => onChange({ ...content, infoSections: s })}
      />
    </Stack>
  );
}

// ── Wi-Fi ────────────────────────────────────────────────────────────────

export function WifiEditor({ content, onChange }: ContentPaneProps) {
  const wifi = content.wifi ?? { ssid: "" };
  const setWifi = (part: Partial<SchemeContent["wifi"]>) =>
    onChange({ ...content, wifi: { ...wifi, ...part } });
  return (
    <Stack spacing={2} sx={{ maxWidth: 480 }}>
      <Typography variant="h6">Wi-Fi</Typography>
      <TextField
        size="small"
        label="Network name (SSID)"
        value={wifi.ssid}
        onChange={(e) => setWifi({ ssid: e.target.value })}
      />
      <TextField
        size="small"
        label="Password"
        value={wifi.password ?? ""}
        onChange={(e) => setWifi({ password: e.target.value || undefined })}
      />
      <FormControlLabel
        control={
          <Switch
            checked={!!wifi.showQr}
            onChange={(e) => setWifi({ showQr: e.target.checked })}
          />
        }
        label="Show a QR code guests can scan to join"
      />
    </Stack>
  );
}

// ── Welcome / Messages ─────────────────────────────────────────────────────

export function WelcomeEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const loc = { supportedLocales, defaultLocale };
  const w = content.welcome ?? { title: "", body: "" };
  const setW = (part: Partial<SchemeContent["welcome"]>) =>
    onChange({ ...content, welcome: { ...w, ...part } });
  return (
    <Stack spacing={2} sx={{ maxWidth: 640 }}>
      <Typography variant="h6">Welcome / Messages</Typography>
      <LocalizedTextField
        label="Title"
        value={w.title}
        {...loc}
        onChange={(v: LocalizedString) => setW({ title: v })}
      />
      <LocalizedTextField
        label="Message"
        value={w.body}
        multiline
        rows={6}
        {...loc}
        onChange={(v: LocalizedString) => setW({ body: v })}
      />
    </Stack>
  );
}

// ── Relatives (Anhöriga) ───────────────────────────────────────────────────

export function RelativesEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const rel = content.relatives ?? { sections: [] };
  const setRel = (part: Partial<SchemeContent["relatives"]>) =>
    onChange({ ...content, relatives: { ...rel, ...part } });
  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Typography variant="h6">Relatives (Anhöriga)</Typography>
      <TextField
        size="small"
        label="Relatives portal URL (optional)"
        placeholder="https://…"
        value={rel.embedUrl ?? ""}
        onChange={(e) =>
          setRel({ embedUrl: e.target.value.trim() || undefined })
        }
        helperText="When set, the Relatives screen embeds this page full-screen instead of the sections below."
        fullWidth
      />
      <Typography variant="subtitle2" color="text.secondary">
        Sections ({rel.sections.length})
      </Typography>
      <LocalizedSectionsEditor
        sections={rel.sections}
        supportedLocales={supportedLocales}
        defaultLocale={defaultLocale}
        onChange={(s) => setRel({ sections: s })}
      />
    </Stack>
  );
}

// ── Instagram ───────────────────────────────────────────────────────────────

export function InstagramEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const loc = { supportedLocales, defaultLocale };
  const ig = content.instagram ?? { handle: "" };
  const setIg = (part: Partial<SchemeContent["instagram"]>) =>
    onChange({ ...content, instagram: { ...ig, ...part } });
  // Legacy back-compat: pre-`display` configs are pure "invite" screens.
  const display: InstagramDisplay = ig.display ?? "invite";
  const feedNeedsConnection = display === "feed" || display === "both";
  const feedCount = ig.feedCount ?? 6;
  return (
    <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
      <Typography variant="h6">Instagram</Typography>
      <Typography variant="body2" color="text.secondary">
        Show the account&apos;s recent posts, a scan-QR follow-us invite, or
        both. The live feed pulls real posts via the Instagram Graph API once
        you connect the account below; the invite needs no connection.
      </Typography>

      <FormControl>
        <FormLabel
          sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}
          id="ig-display-label"
        >
          What this screen shows
        </FormLabel>
        <RadioGroup
          aria-labelledby="ig-display-label"
          value={display}
          onChange={(e) =>
            setIg({ display: e.target.value as InstagramDisplay })
          }
        >
          <FormControlLabel
            value="feed"
            control={<Radio size="small" />}
            label="Feed - the account's recent posts"
          />
          <FormControlLabel
            value="invite"
            control={<Radio size="small" />}
            label="Invite - a scan-QR to follow the account"
          />
          <FormControlLabel
            value="both"
            control={<Radio size="small" />}
            label="Both - feed grid next to the scan-QR"
          />
        </RadioGroup>
      </FormControl>

      <TextField
        size="small"
        label="Handle"
        placeholder="grandhotel"
        value={ig.handle}
        onChange={(e) =>
          setIg({ handle: e.target.value.replace(/^@/, "").trim() })
        }
        helperText="Without the @ - drives the profile link and QR target. Auto-filled when you connect an account."
      />
      <LocalizedTextField
        label="Caption (optional)"
        value={ig.caption ?? ""}
        multiline
        rows={2}
        {...loc}
        onChange={(v: LocalizedString) => setIg({ caption: v || undefined })}
      />

      {feedNeedsConnection && (
        <TextField
          select
          size="small"
          label="Posts in the feed grid"
          value={feedCount}
          onChange={(e) => setIg({ feedCount: Number(e.target.value) })}
          sx={{ maxWidth: 220 }}
        >
          {[3, 4, 6, 8, 9, 12].map((n) => (
            <MenuItem key={n} value={n}>
              {n} posts
            </MenuItem>
          ))}
        </TextField>
      )}

      <Divider />
      <InstagramConnection
        handle={ig.handle}
        onHandle={(h) => setIg({ handle: h })}
        feedNeeded={feedNeedsConnection}
      />
    </Stack>
  );
}

/** Connect / disconnect the premise's Instagram account via OAuth. Opens the
 *  Meta consent flow in a popup and listens for the `foyer:instagram` message
 *  the callback page posts back. */
function InstagramConnection({
  handle,
  onHandle,
  feedNeeded,
}: {
  handle: string;
  onHandle: (handle: string) => void;
  feedNeeded: boolean;
}) {
  const premiseId = usePremiseScope();
  const [status, setStatus] = useState<InstagramConnectionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!premiseId) return;
    try {
      const s = await foyerApi.instagramStatus(premiseId);
      setStatus(s);
      // Keep the handle field in sync with the connected account.
      if (s.connected && s.username && s.username !== handle) {
        onHandle(s.username);
      }
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId]);

  // Listen for the popup's completion message.
  useEffect(() => {
    function onMsg(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const data = e.data as
        | { type?: string; payload?: { ok?: boolean; error?: string } }
        | undefined;
      if (data?.type !== "foyer:instagram") return;
      setLoading(false);
      if (data.payload?.ok) {
        setError(null);
        void refresh();
      } else {
        setError(friendlyError(data.payload?.error));
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId, handle]);

  const connect = () => {
    if (!premiseId) return;
    setError(null);
    setLoading(true);
    const w = 600;
    const h = 720;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      foyerApi.instagramConnectUrl(premiseId),
      "foyer-instagram",
      `popup,width=${w},height=${h},left=${left},top=${top}`
    );
    if (!popup) {
      setLoading(false);
      setError("Pop-up blocked - allow pop-ups for this site and retry.");
    }
  };

  const disconnect = async () => {
    if (!premiseId) return;
    setLoading(true);
    setError(null);
    try {
      await foyerApi.disconnectInstagram(premiseId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  if (!premiseId) {
    return (
      <Alert severity="info" variant="outlined">
        Open this template from a premise to connect an Instagram account.
      </Alert>
    );
  }

  const connected = status?.connected === true;
  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="subtitle2">Connected account</Typography>
        {connected ? (
          <Chip
            size="small"
            color="success"
            label={`@${status?.username ?? handle}`}
          />
        ) : (
          <Chip size="small" variant="outlined" label="Not connected" />
        )}
        {loading && <CircularProgress size={16} />}
      </Stack>

      {connected && status?.expiresAt && (
        <Typography variant="caption" color="text.secondary">
          Access renews automatically. Token valid until{" "}
          {new Date(status.expiresAt).toLocaleDateString()}.
        </Typography>
      )}

      {!connected && feedNeeded && (
        <Alert severity="warning" variant="outlined">
          The feed needs a connected account. Until you connect one, the screen
          falls back to the follow-us invite.
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          variant="outlined"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          onClick={connect}
          disabled={loading}
        >
          {connected ? "Reconnect" : "Connect Instagram"}
        </Button>
        {connected && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={disconnect}
            disabled={loading}
          >
            Disconnect
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Requires an Instagram Business or Creator account. You&apos;ll be sent
        to Instagram to authorise read-only access to the account&apos;s posts.
      </Typography>
    </Stack>
  );
}

function friendlyError(code?: string): string {
  switch (code) {
    case "instagram_not_configured":
      return "Instagram isn't configured on the server yet (missing app credentials).";
    case "invalid_state":
      return "The connect session expired. Please try again.";
    case "oauth_exchange_failed":
      return "Instagram rejected the authorisation. Make sure it's a Business/Creator account and retry.";
    case "premise_not_found":
      return "This premise no longer exists.";
    default:
      return code || "Couldn't connect Instagram. Please try again.";
  }
}

// ── Weather ──────────────────────────────────────────────────────────────────

export function WeatherEditor({ content, onChange }: ContentPaneProps) {
  const w = content.weather ?? {};
  const units = w.units ?? "c";
  return (
    <Stack spacing={2} sx={{ maxWidth: 480 }}>
      <Typography variant="h6">Weather</Typography>
      <Typography variant="body2" color="text.secondary">
        Live forecast for this premise, fetched from its saved map location
        (Settings → Address). If no coordinates are set, the Weather screen
        stays hidden.
      </Typography>
      <TextField
        select
        size="small"
        label="Units"
        value={units}
        onChange={(e) =>
          onChange({
            ...content,
            weather: { ...w, units: e.target.value as "c" | "f" },
          })
        }
        sx={{ maxWidth: 220 }}
      >
        <MenuItem value="c">Celsius (°C)</MenuItem>
        <MenuItem value="f">Fahrenheit (°F)</MenuItem>
      </TextField>
    </Stack>
  );
}

// ── Home cards (Care) ─────────────────────────────────────────────────────

/** The Care home cards whose icon + text can be customised. */
const CARE_CARDS: { id: string; label: string }[] = [
  { id: "tv", label: "TV" },
  { id: "apps", label: "Nöje" },
  { id: "radio", label: "Radio" },
  { id: "info", label: "Info" },
  { id: "relatives", label: "Anhöriga" },
];

export function HomeCardsEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const loc = { supportedLocales, defaultLocale };
  const icons = content.homeIcons ?? {};
  const cards = content.homeCards ?? {};
  const setIcon = (id: string, key: string) =>
    onChange({ ...content, homeIcons: { ...icons, [id]: key } });
  const setCard = (
    id: string,
    part: Partial<{ title: LocalizedString; description: LocalizedString }>
  ) =>
    onChange({
      ...content,
      homeCards: { ...cards, [id]: { ...cards[id], ...part } },
    });
  return (
    <Stack spacing={2.5}>
      <Typography variant="h6">Home cards</Typography>
      <Typography variant="body2" color="text.secondary">
        Customise the icon, title and description of each home card. Leave the
        title or description blank to keep the built-in default. Cards only
        appear when their component is enabled under Components.
      </Typography>
      <Box sx={fieldGrid(320)}>
        {CARE_CARDS.map((card) => (
          <Box
            key={card.id}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {card.label}
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                select
                size="small"
                label="Icon"
                value={icons[card.id] ?? card.id}
                onChange={(e) => setIcon(card.id, e.target.value)}
              >
                {CARE_ICON_OPTIONS.map((o) => (
                  <MenuItem key={o.key} value={o.key}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <LocalizedTextField
                label="Title"
                value={cards[card.id]?.title ?? ""}
                {...loc}
                onChange={(v: LocalizedString) =>
                  setCard(card.id, { title: v || undefined })
                }
              />
              <LocalizedTextField
                label="Description"
                value={cards[card.id]?.description ?? ""}
                multiline
                rows={2}
                {...loc}
                onChange={(v: LocalizedString) =>
                  setCard(card.id, { description: v || undefined })
                }
              />
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

// ── shared helpers ─────────────────────────────────────────────────────────

// ── Promotion slots (Announcement / QR) ────────────────────────────────────

/** Human label per slot component type, for the type picker. */
const SLOT_TYPE_LABEL: Record<SlotType, string> = {
  announcement: "Announcement (text banner)",
  qr: "QR action (link)",
  offer: "Offer (from your catalogue)",
};

/** Size choices the operator can pick for a promotion. */
const SLOT_SIZES: { value: SlotSize; label: string }[] = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

/** Build a fresh, empty fill of the given type (operator supplies data after). */
function defaultSlotFill(type: SlotType): SlotFill {
  if (type === "qr") return { type: "qr", url: "", enabled: true };
  if (type === "offer") return { type: "offer", offerId: "", enabled: true };
  return { type: "announcement", body: "", enabled: true };
}

/** ISO instant → value for a <input type="datetime-local"> (local time, no tz). */
function isoToLocalInput(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value → ISO instant (or undefined when cleared/invalid). */
function localInputToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Promotions pane - renders one card per slot the template declares in its
 * manifest. The operator only fills/toggles content; the template owns where
 * the slot sits and how it's styled. Shown only for templates that expose slots.
 */
export function SlotsEditor({
  content,
  manifest,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps & { manifest: SchemeManifest }) {
  const defs = manifest.slots ?? [];
  const slots = content.slots ?? {};
  const premiseId = usePremiseScope();
  // The offer catalogue, for the picker on an `offer` fill (`POS-4`). Fetched
  // once here rather than per card, and only when this template actually has a
  // slot that takes one - the five care templates declare no slots at all and
  // must never call a hotel route. The premise route answers with the rows this
  // location inherits as well as its own, so a chain-level offer is pickable.
  const takesOffers = defs.some((d) => d.accepts.includes("offer"));
  const [offers, setOffers] = useState<RoomOffer[]>([]);
  useEffect(() => {
    if (!takesOffers || !premiseId) return;
    let alive = true;
    foyerApi
      .listRoomOffers({ kind: "premise", premiseId })
      .then((list) => alive && setOffers(list))
      .catch(() => alive && setOffers([]));
    return () => {
      alive = false;
    };
  }, [takesOffers, premiseId]);
  const setFills = (id: string, fills: SlotFill[]) => {
    const next = { ...slots };
    // An empty list is an empty slot, not a slot holding nothing: leaving the
    // key behind would keep the slot "filled in" everywhere that counts keys.
    if (fills.length === 0) delete next[id];
    else next[id] = fills;
    onChange({ ...content, slots: next });
  };
  if (defs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This template has no promotion slots.
      </Typography>
    );
  }
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Promotions</Typography>
      </Box>
      {defs.map((def) => {
        const fills = slotFills(slots, def.id);
        const write = (next: SlotFill[]) =>
          setFills(def.id, next.filter(Boolean));
        return (
          <Stack key={def.id} spacing={2}>
            {fills.map((fill, i) => (
              <SlotCard
                key={`${def.id}-${i}`}
                def={def}
                index={i}
                total={fills.length}
                fill={fill}
                offers={offers}
                supportedLocales={supportedLocales}
                defaultLocale={defaultLocale}
                onChange={(f) =>
                  write(
                    f
                      ? fills.map((x, n) => (n === i ? f : x))
                      : fills.filter((_, n) => n !== i)
                  )
                }
              />
            ))}
            {fills.length < MAX_SLOT_FILLS && (
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() =>
                    write([...fills, defaultSlotFill(def.accepts[0])])
                  }
                  data-testid={`slot-add-${def.id}`}
                >
                  {fills.length === 0 ? "Add a promotion" : "Add another"}
                </Button>
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

function SlotCard({
  def,
  index,
  total,
  fill,
  offers,
  supportedLocales,
  defaultLocale,
  onChange,
}: {
  def: SchemeSlotDef;
  /** Position in the slot's list, for the header and the remove control. */
  index: number;
  total: number;
  fill: SlotFill | undefined;
  /** The offer catalogue, for an `offer` fill's picker. Empty until it loads. */
  offers: RoomOffer[];
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (next: SlotFill | undefined) => void;
}) {
  const loc = { supportedLocales, defaultLocale };
  // Visibility lives in the Toggles tab (`showPromotions`), not here - this tab
  // is only even shown when that toggle is on. So the card always exposes its
  // fields; the fill is lazily created on first edit.
  const current: SlotFill = fill ?? defaultSlotFill(def.accepts[0]);

  const changeType = (t: SlotType) => {
    // Preserve the size and active window across a type switch; content shapes
    // differ so the type-specific fields reset.
    const base = {
      size: current.size,
      startAt: current.startAt,
      endAt: current.endAt,
    };
    onChange(
      t === "qr"
        ? { type: "qr", url: "", ...base }
        : t === "offer"
          ? { type: "offer", offerId: "", ...base }
          : { type: "announcement", body: "", ...base }
    );
  };

  return (
    <Box
      sx={{
        // Same card language as the Screens board: a surface that sits ON the
        // page with its own header strip, rather than a hairline rectangle that
        // reads as a fieldset. A promotion IS a thing you place, so it should
        // look like an object, not like a group of inputs.
        borderRadius: "14px",
        border: "1px solid",
        borderColor: tokens.divider,
        backgroundColor: tokens.surface,
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
        overflow: "hidden",
      }}
      data-testid={`slot-card-${def.id}`}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: tokens.divider,
          backgroundColor: tokens.canvas,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            sx={{
              flex: 1,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: tokens.inkMuted,
            }}
          >
            {total > 1 ? `${def.label} ${index + 1}` : def.label}
          </Typography>
          <Button
            size="small"
            color="inherit"
            onClick={() => onChange(undefined)}
            sx={{ minWidth: 0, color: tokens.inkFaint }}
            data-testid={`slot-remove-${def.id}-${index}`}
          >
            Remove
          </Button>
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ p: 2 }}>
        {def.accepts.length > 1 && (
          <TextField
            select
            size="small"
            label="Type"
            value={current.type}
            onChange={(e) => changeType(e.target.value as SlotType)}
            sx={{ maxWidth: 320 }}
          >
            {def.accepts.map((t) => (
              <MenuItem key={t} value={t}>
                {SLOT_TYPE_LABEL[t]}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          size="small"
          label="Size"
          value={current.size ?? "medium"}
          onChange={(e) =>
            onChange({ ...current, size: e.target.value as SlotSize })
          }
          sx={{ maxWidth: 320 }}
        >
          {SLOT_SIZES.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>

        {current.type === "announcement" && (
          <>
            <LocalizedTextField
              label="Title / eyebrow (optional)"
              value={current.title}
              {...loc}
              placeholder="Spa weekend"
              onChange={(title) => onChange({ ...current, title })}
            />
            <LocalizedTextField
              label="Body"
              value={current.body}
              {...loc}
              multiline
              rows={2}
              placeholder="20% off treatments this Saturday & Sunday."
              onChange={(body) => onChange({ ...current, body })}
            />
            <AssetField
              label="Image (optional)"
              value={current.imageUrl ?? ""}
              preview
              onChange={(imageUrl) =>
                onChange({ ...current, imageUrl: imageUrl || undefined })
              }
            />
          </>
        )}

        {current.type === "qr" && (
          <>
            <TextField
              size="small"
              fullWidth
              label="Link (URL)"
              value={current.url}
              placeholder="https://hotel.example/menu"
              onChange={(e) => onChange({ ...current, url: e.target.value })}
            />
            <LocalizedTextField
              label="Label (optional)"
              value={current.label}
              {...loc}
              placeholder="View our dinner menu"
              onChange={(label) => onChange({ ...current, label })}
            />
          </>
        )}

        {current.type === "offer" && (
          <>
            <TextField
              select
              size="small"
              label="Offer"
              value={
                offers.some((o) => o.id === current.offerId)
                  ? current.offerId
                  : ""
              }
              onChange={(e) =>
                onChange({ ...current, offerId: e.target.value })
              }
              sx={{ maxWidth: 420 }}
            >
              <MenuItem value="">
                <em>Not chosen</em>
              </MenuItem>
              {offers.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {resolveLocalized(o.title, defaultLocale, defaultLocale) ||
                    o.id}
                  {o.enabled ? "" : " (switched off)"}
                </MenuItem>
              ))}
            </TextField>
            {!current.offerId && (
              <Alert severity="info">
                Pick an offer. Until you do, the slot shows nothing on the TV -
                which is also what happens if the offer you pick is later
                deleted, switched off, or outside its dates. A guest never sees
                a broken promotion.
              </Alert>
            )}
            {!!current.offerId &&
              offers.length > 0 &&
              !offers.some((o) => o.id === current.offerId) && (
                <Alert severity="warning">
                  The offer this slot pointed at is gone from the catalogue, so
                  the TV is showing nothing here. Pick another one.
                </Alert>
              )}
          </>
        )}

        <Box sx={fieldGrid(220)}>
          <TextField
            size="small"
            type="datetime-local"
            label="Show from (optional)"
            InputLabelProps={{ shrink: true }}
            value={isoToLocalInput(current.startAt)}
            onChange={(e) =>
              onChange({ ...current, startAt: localInputToIso(e.target.value) })
            }
          />
          <TextField
            size="small"
            type="datetime-local"
            label="Show until (optional)"
            InputLabelProps={{ shrink: true }}
            value={isoToLocalInput(current.endAt)}
            onChange={(e) =>
              onChange({ ...current, endAt: localInputToIso(e.target.value) })
            }
          />
        </Box>
      </Stack>
    </Box>
  );
}
